import * as uuid from "uuid";

import { DesignAction, IActionBaseAttributes } from "../../../base/models";
import { Draggable, Droppable } from "../../dnd";
import { HoverMenu, HoverMenuItem } from "../../widgets";
import React, { useEffect, useRef, useState } from "react";
import { createAction, readClipboardActionAsync, resetActionIds } from "../../helpers";

import { ActionCollection } from "./ActionCollection";
import { ActionType } from "../../../base/types";
import { ActionsDialog } from "../../../base/register";
import { DndItem } from "../../dnd/models";
import { PageAction } from "./PageAction";
import clsx from "clsx";
import { useDataContext } from "../../page/context";
import { useDesignContext } from "../../../providers";
import { useKeyboardHandler } from "../../hooks";
import { useToaster } from "../../../layout/toast";

export const PageActionDesigner = (props: IActionBaseAttributes) => {
    const { action, onDesignChange, className, children } = props;
    const ref = useRef<HTMLInputElement>(null)
    const [dialog, showDialog] = useState<boolean>(false);

    const { showToaster } = useToaster()
    const { keyCode, reset } = useKeyboardHandler<HTMLInputElement>(ref)
    const { action: { setContext } } = useDesignContext()
    const { dataSource } = useDataContext()

    useEffect(() => {
        if (!keyCode) return;
        reset()

        const { actionId } = action
        switch (keyCode) {
            case "Delete":
                onDesignChange({ type: DesignAction.DeleteAction, actionId });
                break;
            case "ShiftUp":
                onDesignChange({ type: DesignAction.ShiftActionUp, actionId });
                break;
            case "ShiftDown":
                onDesignChange({ type: DesignAction.ShiftActionDown, actionId });
                break;
            case "Copy":
                copyActionAsync()
                break;
            case "Cut":
                cutActionAsync()
                break;
            case "Paste":
                pasteActionAsync()
                break;
        }
        // eslint-disable-next-line
    }, [keyCode])

    const cutActionAsync = async () => {
        await copyActionAsync()
        onDesignChange({ type: DesignAction.DeleteAction, actionId: action.actionId })
    }

    const copyActionAsync = async () => {
        const json = JSON.stringify({ ...action, actionId: uuid.v1() });
        await navigator.clipboard.writeText("::PAGE-ACTION:: " + json);
        showToaster('Action copied to clipboard.')
    }

    const pasteActionAsync = async () => {
        const actionToPaste = await readClipboardActionAsync()
        if (!actionToPaste) {
            showToaster("Not a valid page action")
            return
        }

        onDesignChange({
            type: DesignAction.InsertAfterAction,
            actionId: action.actionId,
            action: resetActionIds(actionToPaste)
        })
    }

    const onFocusActionSelection = (e: React.FocusEvent) => {
        e.stopPropagation()
        e.preventDefault()

        if (!ref.current)
            return

        const elements = window.document.getElementsByClassName("pfx-control__selector")
        for (let i = 0; i < elements.length; i++) {
            const element = elements.item(i) as HTMLInputElement | null;
            if (element) {
                element.checked = false
            }
        }

        if (!ref.current.checked) {
            ref.current.checked = true
            setContext({ element: action, dataSource })
        }
    }

    const onClickPageActionElement = (e: React.MouseEvent) => {
        e.stopPropagation()
        e.preventDefault()

        if (ref.current) {
            ref.current.focus()
            ref.current.checked = true
            setContext({ element: action, dataSource })
        }
    }

    const onSelectAction = (actionType: ActionType) => {
        setTimeout(() => {
            const newAction = createAction(actionType)

            onDesignChange({
                type: DesignAction.InsertFirstAction,
                actionId: action.actionId,
                action: newAction
            });
        }, 50)
    }

    const onDropItem = (item: DndItem) => {
        if (item.type !== "Action") return

        onDesignChange({
            type: DesignAction.DropOnFirstAction,
            actionId: action.actionId,
            action: item.action
        });
    }

    const onHoverMenuItemClick = (item: HoverMenuItem) => {
        const { actionId } = action

        switch (item) {
            case "Cut":
                cutActionAsync()
                break;
            case "Copy":
                copyActionAsync()
                break;
            case "Paste":
                pasteActionAsync()
                break;
            case "Delete":
                onDesignChange({ type: DesignAction.DeleteAction, actionId });
                break;
        }
    }

    const dataAttrs = {
        "data-action-id": action.actionId,
        "data-action-name": action.name,
        "data-action-type": action.type
    }

    const actionClass = clsx("pfx-control")

    return (
        <div {...dataAttrs}
            title={`${action.type}`}
            className={actionClass}
            onClick={onClickPageActionElement}>

            <HoverMenu onClick={onHoverMenuItemClick} />
            <Draggable className="pfx-control__body" item={{ type: "Action", action }}>
                <input type="radio"
                    ref={ref}
                    id={action.actionId}
                    name="selector"
                    className="pfx-control__selector"
                    onFocusCapture={onFocusActionSelection} />

                <PageAction {...props} className={
                    clsx("page-action", className)}>
                    <Droppable
                        onClick={(e) => {
                            e.stopPropagation();
                            showDialog(true)
                        }}
                        onDropItem={onDropItem}
                        layout={"Auto"}
                        title={`Click to insert a new component`} />

                    <ActionCollection
                        action={action}
                        actions={action.actions || []}
                        onDesignChange={onDesignChange} />
                </PageAction>
            </Draggable>

            <>
                {/* Droppable from child actions */}
                {children}
            </>

            <ActionsDialog
                show={dialog}
                onSelect={onSelectAction}
                onHideModal={() => showDialog(false)} />
        </div>)
}